import { useMemo, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import {
  CheckCircle2, AlertCircle, Circle, ShieldCheck, RotateCcw, XCircle,
} from "lucide-react";
import LeviCharacter from "@/components/LeviCharacter";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Slice 2Q · Levi-Guided Job Closeout.
// Readiness is derived from data already loaded by the parent page — no
// new backend endpoint is required. State is one of:
//   in_progress · needs_attention · ready_to_close · closed
// The contractor's explicit action is the ONLY thing that closes or
// reopens the project. Levi never calls these endpoints.

const STATE_TONE = {
  in_progress:     { bg: "bg-slate-100 text-slate-800", Icon: Circle },
  needs_attention: { bg: "bg-amber-100 text-amber-900", Icon: AlertCircle },
  ready_to_close:  { bg: "bg-emerald-100 text-emerald-900", Icon: CheckCircle2 },
  closed:          { bg: "bg-[var(--jc-navy)]/10 text-[var(--jc-navy)]", Icon: ShieldCheck },
};

const ITEM_TONE = {
  ok:      "text-emerald-800",
  warn:    "text-amber-800",
  missing: "text-rose-800",
  na:      "text-[var(--jc-text-soft)]",
};

const ITEM_ICON = {
  ok:      CheckCircle2,
  warn:    AlertCircle,
  missing: XCircle,
  na:      Circle,
};

function deriveReadiness(project, closeoutPacket, invoices, workOrders) {
  const items = [];
  // 1. Work completion summary — packet-level or unlocked
  const cp = closeoutPacket || null;
  const summary = (cp?.work_completed_summary || "").trim();
  items.push({
    key: "work_summary",
    status: summary ? "ok" : (cp ? "warn" : "missing"),
  });
  // 2. Final photos
  const photoCount = Array.isArray(cp?.final_photos) ? cp.final_photos.length : 0;
  items.push({
    key: "final_photos",
    status: photoCount > 0 ? "ok" : "warn",
  });
  // 3. Unresolved punch-list — from open work orders + explicit notes
  const openWos = (workOrders || []).filter(
    (w) => (w.status || "").toLowerCase() !== "complete" && (w.status || "").toLowerCase() !== "completed",
  );
  const punch = (cp?.final_punch_list_notes || "").trim();
  items.push({
    key: "punch_list",
    status: openWos.length === 0 ? (punch ? "warn" : "ok") : "warn",
    note: openWos.length > 0 ? String(openWos.length) : (punch ? "1" : ""),
  });
  // 4. Workmanship warranty
  items.push({
    key: "workmanship_warranty",
    status: (cp?.workmanship_warranty_terms || "").trim() ? "ok" : "warn",
  });
  // 5. Manufacturer warranty
  items.push({
    key: "manufacturer_warranty",
    status: (cp?.manufacturer_warranty_notes || "").trim() ? "ok" : "na",
  });
  // 6. Final invoice / payment status.
  // Slice 2Q · Correction Pass A — Defect 1. Invoices persist their paid
  // flag as `payment_status` (see backend create_invoice / mark_paid);
  // read that field so a genuinely paid final invoice resolves to `ok`.
  // Legacy `status` fallback is kept for older records that predate the
  // convention.
  const paidLike = new Set(["paid", "customer_paid", "paid_in_full", "completed"]);
  const finalInv = (invoices || []).find((i) =>
    paidLike.has((i.payment_status || i.status || "").toLowerCase()),
  );
  items.push({
    key: "final_invoice",
    status: finalInv ? "ok" : ((invoices || []).length > 0 ? "warn" : "warn"),
  });
  // 7. Customer-facing closeout documents (packet exists + not private)
  const sharingState = cp?.sharing?.state || (cp ? "private" : null);
  items.push({
    key: "closeout_docs",
    status: sharingState && sharingState !== "private" ? "ok"
             : (cp ? "warn" : "missing"),
  });

  // Aggregate
  const missing = items.filter((i) => i.status === "missing").length;
  const warn = items.filter((i) => i.status === "warn").length;
  let state = "in_progress";
  if ((project?.status || "").toLowerCase() === "closed") state = "closed";
  else if (missing > 0) state = "needs_attention";
  else if (warn > 0) state = "needs_attention";
  else state = "ready_to_close";
  return { state, items, missing, warn };
}

function leviMessage(state, missing, warn, t) {
  if (state === "closed") return t("project_closeout.levi.closed");
  if (state === "ready_to_close") return t("project_closeout.levi.ready");
  if (missing > 0) return t("project_closeout.levi.missing");
  if (warn > 0) return t("project_closeout.levi.warn");
  return t("project_closeout.levi.progress");
}

export default function ProjectCloseoutSection({
  project, closeoutPackets, invoices, workOrders, onProjectRefresh,
}) {
  const { t } = useLang();
  const [dialog, setDialog] = useState(null); // "close" | "reopen"
  const [busy, setBusy] = useState(false);
  const cp = (closeoutPackets && closeoutPackets[0]) || null;

  const { state, items, missing, warn } = useMemo(
    () => deriveReadiness(project, cp, invoices, workOrders),
    [project, cp, invoices, workOrders],
  );

  const tone = STATE_TONE[state] || STATE_TONE.in_progress;
  const StatePillIcon = tone.Icon;

  const doClose = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/close`);
      toast.success(t("project_closeout.toast.closed"));
      if (onProjectRefresh) await onProjectRefresh();
    } catch {
      toast.error(t("project_closeout.toast.close_failed"));
    } finally { setBusy(false); setDialog(null); }
  };

  const doReopen = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/reopen`);
      toast.success(t("project_closeout.toast.reopened"));
      if (onProjectRefresh) await onProjectRefresh();
    } catch {
      toast.error(t("project_closeout.toast.reopen_failed"));
    } finally { setBusy(false); setDialog(null); }
  };

  return (
    <section
      data-testid="project-closeout-section"
      data-state={state}
      className="jc-card p-4 sm:p-5 mb-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="overline">{t("project_closeout.heading")}</div>
          <div className="text-xs text-[var(--jc-text-soft)] mt-0.5">
            {t("project_closeout.subheading")}
          </div>
        </div>
        <span
          data-testid="project-closeout-state-pill"
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${tone.bg}`}
        >
          <StatePillIcon size={12} className="opacity-80" />
          {t(`project_closeout.state.${state}`)}
        </span>
      </div>

      {/* Levi guidance */}
      <div
        data-testid="project-closeout-levi"
        className="rounded-md border border-[var(--jc-border)] bg-[var(--jc-bg)] p-3 flex items-start gap-3 mb-4"
      >
        <div className="shrink-0 w-10 h-10">
          <LeviCharacter size={40} />
        </div>
        <div className="text-sm leading-relaxed">
          <div className="font-semibold text-[var(--jc-text)]">
            {t("project_closeout.levi_intro")}
          </div>
          <div className="text-[var(--jc-text-soft)] mt-1">
            {leviMessage(state, missing, warn, t)}
          </div>
          <div className="text-[10px] text-[var(--jc-text-soft)] mt-1 italic">
            {t("project_closeout.levi_disclaimer")}
          </div>
        </div>
      </div>

      {/* Checklist */}
      <ul
        data-testid="project-closeout-checklist"
        className="grid sm:grid-cols-2 gap-2 mb-4"
      >
        {items.map((it) => {
          const Icon = ITEM_ICON[it.status] || Circle;
          return (
            <li
              key={it.key}
              data-testid={`project-closeout-item-${it.key}`}
              data-status={it.status}
              className="flex items-start gap-2 text-sm py-1"
            >
              <Icon size={14} className={`mt-0.5 shrink-0 ${ITEM_TONE[it.status]}`} />
              <div className="min-w-0">
                <div className="text-[var(--jc-text)]">
                  {t(`project_closeout.item.${it.key}`)}
                </div>
                <div className={`text-xs ${ITEM_TONE[it.status]}`}>
                  {t(`project_closeout.status.${it.status}`)}
                  {it.note ? ` · ${it.note}` : ""}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Close / Reopen action */}
      <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-[var(--jc-border)]">
        {state !== "closed" ? (
          <>
            <button
              data-testid="project-close-btn"
              onClick={() => setDialog("close")}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
            >
              <ShieldCheck size={14} />
              {t("project_closeout.action.close")}
            </button>
            {(missing > 0 || warn > 0) && (
              <span className="text-xs text-[var(--jc-text-soft)]">
                {t("project_closeout.non_blocking_note")}
              </span>
            )}
          </>
        ) : (
          <>
            <div className="text-xs text-[var(--jc-text-soft)]">
              {t("project_closeout.closed_at_prefix")}{" "}
              <span className="font-mono-jc" data-testid="project-closed-at">
                {formatDate(project.closed_at)}
              </span>
            </div>
            <button
              data-testid="project-reopen-btn"
              onClick={() => setDialog("reopen")}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)] disabled:opacity-60"
            >
              <RotateCcw size={14} />
              {t("project_closeout.action.reopen")}
            </button>
          </>
        )}
      </div>

      {/* Close confirmation */}
      <AlertDialog open={dialog === "close"} onOpenChange={(o) => (o ? null : setDialog(null))}>
        <AlertDialogContent data-testid="project-close-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("project_closeout.confirm.close_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("project_closeout.confirm.close_body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {(missing > 0 || warn > 0) && (
            <div
              data-testid="project-close-warnings"
              className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900"
            >
              {t("project_closeout.confirm.warnings_prefix")}{" "}
              <span className="font-semibold">
                {missing + warn}{" "}
                {t("project_closeout.confirm.warnings_suffix")}
              </span>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="project-close-cancel" disabled={busy}>
              {t("project_closeout.confirm.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction data-testid="project-close-confirm" onClick={doClose} disabled={busy}>
              {t("project_closeout.confirm.close_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reopen confirmation */}
      <AlertDialog open={dialog === "reopen"} onOpenChange={(o) => (o ? null : setDialog(null))}>
        <AlertDialogContent data-testid="project-reopen-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("project_closeout.confirm.reopen_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("project_closeout.confirm.reopen_body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="project-reopen-cancel" disabled={busy}>
              {t("project_closeout.confirm.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction data-testid="project-reopen-confirm" onClick={doReopen} disabled={busy}>
              {t("project_closeout.confirm.reopen_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
